import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ListingCard from '../components/ListingCard';
import BesoinCard from '../components/BesoinCard';
import FilterBar from '../components/FilterBar';
import AgencesCarousel from '../components/AgencesCarousel';
import ListingsMap from '../components/ListingsMap';
import api from '../utils/api';
import { SEO_LANDING_LINKS } from '../data/seoLandingPages';
import './Home.css';
import './Besoins.css';

const VILLES_HERO = ['Conakry', 'Kindia', 'Labé', 'Kankan', 'Nzérékoré', 'Boké', 'Mamou'];

const TYPES = [
  { value: '', label: 'Tout' },
  { value: 'location', label: 'Location' },
  { value: 'vente-maison', label: 'Achat' },
  { value: 'terrain', label: 'Terrain' },
];

const FILTRE_LABELS = {
  type: 'Type',
  typeLogement: 'Logement',
  ville: 'Ville',
  commune: 'Commune',
  quartier: 'Quartier',
  prixMin: 'Prix min',
  prixMax: 'Prix max',
  chambresMin: 'Chambres',
  surfaceMin: 'Surface min',
  surfaceMax: 'Surface max',
  surfaceTerrainMin: 'Terrain min',
  surfaceTerrainMax: 'Terrain max',
  etat: 'Etat',
  negotiable: 'Negociable',
  verifie: 'Verifiee',
  photosOnly: 'Photos',
  tri: 'Tri',
};

function formatFiltreLabel(key, value) {
  const label = FILTRE_LABELS[key] || key;
  if (value === 'true') return label;
  return `${label}: ${value}`;
}

const IcoShield = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--vert)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    <polyline points="9 12 11 14 15 10"/>
  </svg>
);
const IcoZap = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--vert)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
);
const IcoLock = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--vert)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);
const IcoMapPin = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--vert)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);

const POURQUOI = [
  { icon: <IcoShield />, titre: 'Annonces fiables', desc: 'Chaque annonce publiée engage la responsabilité du propriétaire. Les annonces frauduleuses sont signalables et supprimées rapidement.' },
  { icon: <IcoZap />, titre: 'Réponse rapide', desc: 'Contactez directement les propriétaires et agences par WhatsApp ou message en un clic.' },
  { icon: <IcoLock />, titre: '100% sécurisé', desc: 'Vos données sont protégées. Nous ne partageons jamais vos informations sans votre accord.' },
  { icon: <IcoMapPin />, titre: 'Toute la Guinée', desc: 'De Conakry à Nzérékoré, trouvez votre bien partout dans le pays en quelques secondes.' },
];

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const [annonces, setAnnonces] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, pages: 1, page: 1 });
  const [filtres, setFiltres] = useState({});
  const [typeActif, setTypeActif] = useState('');
  const [heroRecherche, setHeroRecherche] = useState({ ville: '', type: '' });
  const [vue, setVue] = useState('liste');
  const [annoncesMap, setAnnoncesMap] = useState([]);
  const [derniersBesoins, setDerniersBesoins] = useState([]);
  const [chargementMap, setChargementMap] = useState(false);

  useEffect(() => {
    api.get('/besoins?limite=3').then(res => setDerniersBesoins(res.data.besoins || [])).catch(() => {});
  }, []);
  const filtresUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return Object.fromEntries(
      [...params.entries()].filter(([, value]) => value.trim() !== '')
    );
  }, [location.search]);

  const charger = useCallback(async (params = {}, page = 1) => {
    setChargement(true);
    try {
      const query = new URLSearchParams(
        Object.fromEntries(Object.entries({ ...params, page, limite: 12 }).filter(([, v]) => v !== ''))
      );
      const res = await api.get(`/listings?${query}`);
      setAnnonces(res.data.annonces);
      setPagination({ total: res.data.total, pages: res.data.pages, page: res.data.page });
    } catch {
      setAnnonces([]);
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    setFiltres(filtresUrl);
    setTypeActif(filtresUrl.type || '');
    setHeroRecherche({
      ville: filtresUrl.ville || '',
      type: filtresUrl.type || '',
    });
    setAnnoncesMap([]);
    charger(filtresUrl, 1);
  }, [charger, filtresUrl]);

  const handleFiltrer = (f) => {
    const clean = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ''));
    setFiltres(clean);
    setTypeActif(f.type || '');
    charger(clean, 1);
  };

  const handleType = (type) => {
    setTypeActif(type);
    const newFiltres = { ...filtres, type };
    if (!type) delete newFiltres.type;
    if (type === 'terrain') {
      delete newFiltres.typeLogement;
      delete newFiltres.chambresMin;
    }
    if (type !== 'location') delete newFiltres.periodeLocation;
    setFiltres(newFiltres);
    setHeroRecherche(prev => ({ ...prev, type }));
    setAnnoncesMap([]);
    charger(newFiltres, 1);
  };

  const handleHeroSearch = (e) => {
    e.preventDefault();
    const clean = Object.fromEntries(Object.entries(heroRecherche).filter(([, v]) => v !== ''));
    setFiltres(clean);
    setTypeActif(heroRecherche.type || '');
    charger(clean, 1);
    document.getElementById('annonces-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePage = (p) => {
    charger(filtres, p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const basculerVueCarte = async () => {
    if (vue === 'liste') {
      setVue('carte');
      if (annoncesMap.length === 0) {
        setChargementMap(true);
        try {
          const query = new URLSearchParams(
            Object.fromEntries(Object.entries({ ...filtres, limite: 500 }).filter(([, v]) => v !== ''))
          );
          const res = await api.get(`/listings?${query}`);
          setAnnoncesMap(res.data.annonces);
        } catch {
          setAnnoncesMap([]);
        } finally {
          setChargementMap(false);
        }
      }
    } else {
      setVue('liste');
    }
  };

  // Recharger la carte si les filtres changent
  const handleFiltrerAvecMap = (f) => {
    setAnnoncesMap([]);
    handleFiltrer(f);
  };

  const filtresActifs = Object.entries(filtres).filter(([k, v]) => v !== '' && !(k === 'tri' && v === 'recent'));

  return (
    <div className="home-page">

      {/* ══════════════════════════════════════
          HERO — Image fond + contenu centré
      ══════════════════════════════════════ */}
      <section className="hero">
        <div className="hero-bg-img" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/brand/hero-brand.webp)` }} />
        <div className="hero-bg-overlay" />
        <div className="hero-bg-grid" />

        <div className="hero-content">
          <div className="hero-badge">
            <span className="hero-badge-dot" />
            🇬🇳 L'immobilier partout en Guinée
          </div>

          <h1>
            Trouvez votre{' '}
            <span className="hero-highlight">bien idéal</span>
            <br />en Guinée
          </h1>

          <p className="hero-sub">
            Maisons, villas, terrains et locations à Conakry et dans tout le pays.<br />
            Consultez les annonces disponibles et contactez directement les propriétaires.
          </p>

          {/* Barre de recherche */}
          <form className="hero-search" onSubmit={handleHeroSearch}>
            <div className="hs-field">
              <span className="hs-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                  <line x1="7" y1="7" x2="7.01" y2="7"/>
                </svg>
              </span>
              <select
                id="hero-search-type"
                name="type"
                aria-label="Type de bien"
                value={heroRecherche.type}
                onChange={e => setHeroRecherche(p => ({ ...p, type: e.target.value }))}
              >
                <option value="">Tous les types</option>
                <option value="location">Location</option>
                <option value="vente-maison">Achat maison</option>
                <option value="terrain">Terrain</option>
              </select>
            </div>
            <div className="hs-divider" />
            <div className="hs-field">
              <span className="hs-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
              </span>
              <select
                id="hero-search-ville"
                name="ville"
                aria-label="Ville"
                value={heroRecherche.ville}
                onChange={e => setHeroRecherche(p => ({ ...p, ville: e.target.value }))}
              >
                <option value="">Toutes les villes</option>
                {VILLES_HERO.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <button type="submit" className="hs-btn">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              Rechercher
            </button>
          </form>

          {/* Quartiers populaires */}
          <div className="hero-quick">
            <span>Populaire :</span>
            {['Kipé', 'Hamdallaye', 'Ratoma', 'Nongo', 'Bambeto'].map(q => (
              <button key={q} className="hero-quick-tag"
                onClick={() => {
                  setFiltres({ quartier: q });
                  charger({ quartier: q }, 1);
                  document.getElementById('annonces-section')?.scrollIntoView({ behavior: 'smooth' });
                }}>
                {q}
              </button>
            ))}
          </div>

          {/* Stats */}
          <div className="hero-stats">
            <div className="hero-stat">
              <span className="hero-stat-num">{chargement ? '—' : pagination.total}</span>
              <span className="hero-stat-label">Annonces actives</span>
            </div>
            <div className="hero-stat-sep" />
            <div className="hero-stat">
              <span className="hero-stat-num">Direct</span>
              <span className="hero-stat-label">Contact propriétaire</span>
            </div>
            <div className="hero-stat-sep" />
            <div className="hero-stat">
              <span className="hero-stat-num">Gratuit</span>
              <span className="hero-stat-label">Pour les particuliers</span>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          POURQUOI DÉMARCHEUR
      ══════════════════════════════════════ */}
      <section className="section-pourquoi">
        <div className="container">
          <div className="section-label">Pourquoi nous choisir</div>
          <h2 className="section-titre">La référence immobilière en Guinée</h2>
          <div className="pourquoi-grid">
            {POURQUOI.map((p, i) => (
              <div key={i} className="pourquoi-card">
                <div className="pourquoi-icon">{p.icon}</div>
                <h3>{p.titre}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FILTRES
      ══════════════════════════════════════ */}
      <section className="section-securite">
        <div className="container securite-inner">
          <div className="securite-copy">
            <div className="section-label">Securite</div>
            <h2 className="section-titre">Achetez, louez et vendez avec les bons reflexes</h2>
            <p>
              Demarcheur facilite le contact direct, mais chaque visiteur doit rester prudent avant tout paiement ou engagement.
            </p>
          </div>
          <div className="securite-rules">
            {[
              ['Visitez avant de payer', 'Ne versez jamais d argent avant une visite ou une verification serieuse du proprietaire.'],
              ['Verifiez les documents', 'Pour un terrain ou une vente, demandez les pieces et confirmez leur validite.'],
              ['Gardez les echanges traces', 'Utilisez WhatsApp, email ou message afin de garder une preuve des discussions.'],
              ['Signalez les annonces suspectes', 'Chaque page annonce permet de signaler une fraude, un doublon ou un bien deja indisponible.'],
            ].map(([titre, texte]) => (
              <div className="securite-rule" key={titre}>
                <span className="securite-check">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                </span>
                <div>
                  <strong>{titre}</strong>
                  <p>{texte}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="home-guides">
        <div className="container">
          <div className="home-guides-head">
            <div>
              <span className="section-label">Guides pratiques</span>
              <h2>Préparez votre projet immobilier en Guinée</h2>
              <p>Des recherches utiles pour acheter, louer ou investir avec plus de méthode, surtout depuis l’étranger.</p>
            </div>
            <Link to="/diaspora" className="btn btn-secondary">Guide diaspora</Link>
          </div>
          <div className="home-guides-grid">
            {SEO_LANDING_LINKS.map(item => (
              <Link to={item.href} className="home-guide-card" key={item.slug}>
                <span>Guide</span>
                <strong>{item.title}</strong>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section-filtres">
        <div className="container">
          <FilterBar
            key={JSON.stringify(filtres)}
            initialFilters={filtres}
            onFiltrer={handleFiltrerAvecMap}
          />
        </div>
      </section>


      {/* ══════════════════════════════════════
          ANNONCES
      ══════════════════════════════════════ */}
      <section className="page" id="annonces-section">
        <div className="container">

          <div className="annonces-top-bar">
            <div className="types-rapides">
              {TYPES.map(t => (
                <button key={t.value} className={`type-pill ${typeActif === t.value ? 'actif' : ''}`} onClick={() => handleType(t.value)}>
                  {t.value === 'vente-maison' ? 'Maisons à vendre' : t.label}
                </button>
              ))}
            </div>
            <div className="annonces-top-right">
              <p className="resultats-count">
                <strong>{pagination.total}</strong> annonce{pagination.total !== 1 ? 's' : ''}
              </p>
              <div className="vue-toggle">
                <button
                  className={`vue-btn ${vue === 'liste' ? 'actif' : ''}`}
                  onClick={() => setVue('liste')}
                  title="Vue liste"
                  aria-label="Vue liste"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
                    <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                  </svg>
                  Liste
                </button>
                <button
                  className={`vue-btn ${vue === 'carte' ? 'actif' : ''}`}
                  onClick={basculerVueCarte}
                  title="Vue carte"
                  aria-label="Vue carte"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                  </svg>
                  Carte
                </button>
              </div>
            </div>
          </div>

          {filtresActifs.length > 0 && (
            <div className="active-filters">
              <span>Recherche active</span>
              {filtresActifs.slice(0, 8).map(([k, v]) => (
                <button
                  key={k}
                  type="button"
                  className="active-filter-chip"
                  onClick={() => {
                    const next = { ...filtres };
                    delete next[k];
                    setFiltres(next);
                    charger(next, 1);
                    setAnnoncesMap([]);
                  }}
                  title="Retirer ce filtre"
                >
                  {formatFiltreLabel(k, v)} x
                </button>
              ))}
            </div>
          )}

          {vue === 'carte' ? (
            chargementMap ? (
              <div className="map-chargement">Chargement de la carte...</div>
            ) : (
              <ListingsMap annonces={annoncesMap.length > 0 ? annoncesMap : annonces} />
            )
          ) : chargement ? (
            <div className="grille-annonces">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="skeleton-card">
                  <div className="skeleton-img" />
                  <div className="skeleton-body">
                    <div className="skeleton-line sk-w60" />
                    <div className="skeleton-line sk-w40" />
                    <div className="skeleton-line sk-w80" />
                    <div className="skeleton-line sk-w50" />
                  </div>
                </div>
              ))}
            </div>
          ) : annonces.length === 0 ? (
            <div className="vide-state">
              <div className="vide-icon">
                <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </div>
              <h3>Aucune annonce trouvée</h3>
              <p>Modifiez vos critères de recherche ou revenez plus tard.</p>
            </div>
          ) : (
            <>
              <div className="grille-annonces">
                {annonces.map(a => <ListingCard key={a._id} annonce={a} />)}
              </div>
              {pagination.pages > 1 && (
                <div className="pagination">
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
                    <button key={p} className={`btn btn-sm ${p === pagination.page ? 'btn-primary' : 'btn-secondary'}`} onClick={() => handlePage(p)}>
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <section className="home-needs">
        <div className="container home-needs-inner">
          <div className="home-needs-copy">
            <span className="section-label">Nouveau sur Démarcheur</span>
            <h2>Vous cherchez un bien précis&nbsp;?</h2>
            <p>Publiez gratuitement votre besoin. Les propriétaires et agences disposant d’un bien correspondant pourront vous contacter directement.</p>
            <div className="home-needs-actions">
              <Link to="/publier-besoin" className="btn btn-or btn-lg">Publier ma recherche</Link>
              <Link to="/besoins" className="btn btn-secondary btn-lg">Voir les demandes</Link>
            </div>
          </div>
          <div className="home-needs-steps">
            <div><strong>1</strong><span>Décrivez le bien recherché</span></div>
            <div><strong>2</strong><span>Votre demande est vérifiée</span></div>
            <div><strong>3</strong><span>Recevez des propositions</span></div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          CAROUSEL AGENCES PARTENAIRES
      ══════════════════════════════════════ */}
      {derniersBesoins.length > 0 && (
        <section className="home-latest-needs">
          <div className="container">
            <div className="home-latest-needs-head">
              <div><span className="section-label">Opportunités pour les propriétaires</span><h2>Ce que les clients recherchent actuellement</h2><p>Vous possédez un bien correspondant ? Contactez directement le demandeur.</p></div>
              <Link to="/besoins" className="btn btn-secondary">Voir toutes les demandes</Link>
            </div>
            <div className="besoins-grid">{derniersBesoins.map(besoin => <BesoinCard key={besoin._id} besoin={besoin} />)}</div>
          </div>
        </section>
      )}

      <AgencesCarousel />

      {/* ══════════════════════════════════════
          CTA AGENT — avec image
      ══════════════════════════════════════ */}
      <section className="home-cta">
        <div className="container home-cta-inner">
          <div className="cta-text">
            <h2>Vous avez un bien à louer ou à vendre ?</h2>
            <p>Présentez votre bien aux personnes qui recherchent une maison, un terrain ou une location partout en Guinée.</p>
            <div className="cta-actions">
              <Link to="/inscription" className="btn btn-or btn-lg">Publier gratuitement →</Link>
              <Link to="/tarifs" className="btn btn-outline-white btn-lg">Voir les offres pro</Link>
            </div>
            <div className="cta-reassurance">
              <span>✓ Gratuit pour les particuliers</span>
              <span>✓ Activation sous 24h</span>
              <span>✓ Support dédié</span>
            </div>
          </div>
          <div className="cta-img-wrap">
            <img src={`${process.env.PUBLIC_URL}/brand/social-realestate.webp`} alt="Démarcheur immobilier Guinée" className="cta-img" loading="lazy" decoding="async" />
          </div>
        </div>
      </section>

    </div>
  );
}
